"use client";

import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableItem from "./TableItem";
import CampaignDetailsModal from "./CampaignDetailsModal";

const Campaigns = () => {
  const [modal_center, setmodal_center] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  document.title = "Campaigns | Tafaseel Admin";

  // Fetch campaigns from API
  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await Api.get("/admin/campaigns", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setCampaigns(response.data.data);
        setFilteredData([]);
      } else {
        toast.error(response.data.message || "Failed to fetch campaigns", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast.error("Network error. Please check your connection.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch campaigns needing approval
  const fetchCampaignsNeedingApproval = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await Api.get("/admin/campaigns/need-approval", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setCampaigns(response.data.data);
        setFilteredData([]);
        setStatusFilter("need_approve");
      } else {
        toast.error(
          response.data.message || "Failed to fetch campaigns needing approval",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error fetching campaigns needing approval:", error);
      toast.error("Network error. Please check your connection.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  // Load campaigns on component mount
  useEffect(() => {
    fetchCampaigns();
  }, []);

  function tog_center() {
    setmodal_center(!modal_center);
    setSelectedCampaign({});
  }

  const handleShow = async (campaign) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.get(`/admin/campaigns/${campaign.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setSelectedCampaign(response.data.data);
        setmodal_center(true);
      } else {
        toast.error(
          response.data.message || "Failed to fetch campaign details",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error fetching campaign details:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleDelete = async (campaignId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this campaign? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await Api.delete(`/admin/campaigns/${campaignId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        toast.success("Campaign deleted successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        // Remove campaign from local state
        setCampaigns(
          campaigns.filter((campaign) => campaign.id !== campaignId)
        );
        setFilteredData(
          filteredData.filter((campaign) => campaign.id !== campaignId)
        );
      } else {
        toast.error(response.data.message || "Failed to delete campaign", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (query === "" && statusFilter === "") {
      setFilteredData([]);
      setCurrentPage(1);
      return;
    }

    let filtered = campaigns;

    // Apply search filter
    if (query !== "") {
      filtered = filtered.filter(
        (campaign) =>
          (campaign.title && campaign.title.toLowerCase().includes(query)) ||
          (campaign.user?.name &&
            campaign.user.name.toLowerCase().includes(query)) ||
          (campaign.user?.email &&
            campaign.user.email.toLowerCase().includes(query)) ||
          (campaign.location &&
            campaign.location.toLowerCase().includes(query)) ||
          (campaign.category &&
            campaign.category.name &&
            campaign.category.name.toLowerCase().includes(query)) ||
          (campaign.id && campaign.id.toString().includes(query))
      );
    }

    // Apply status filter
    if (statusFilter !== "") {
      filtered = filtered.filter(
        (campaign) => campaign.status === statusFilter
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);

    if (status === "" && searchQuery === "") {
      setFilteredData([]);
      setCurrentPage(1);
      return;
    }

    let filtered = campaigns;

    // Apply status filter
    if (status !== "") {
      filtered = filtered.filter((campaign) => campaign.status === status);
    }

    // Apply search filter
    if (searchQuery !== "") {
      filtered = filtered.filter(
        (campaign) =>
          (campaign.title &&
            campaign.title.toLowerCase().includes(searchQuery)) ||
          (campaign.user?.name &&
            campaign.user.name.toLowerCase().includes(searchQuery)) ||
          (campaign.user?.email &&
            campaign.user.email.toLowerCase().includes(searchQuery)) ||
          (campaign.location &&
            campaign.location.toLowerCase().includes(searchQuery)) ||
          (campaign.category &&
            campaign.category.name &&
            campaign.category.name.toLowerCase().includes(searchQuery)) ||
          (campaign.id && campaign.id.toString().includes(searchQuery))
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleStatusUpdate = (campaignId, newStatus) => {
    // Update campaign status in local state
    setCampaigns(
      campaigns.map((campaign) =>
        campaign.id === campaignId
          ? { ...campaign, status: newStatus }
          : campaign
      )
    );

    if (filteredData.length > 0) {
      setFilteredData(
        filteredData.map((campaign) =>
          campaign.id === campaignId
            ? { ...campaign, status: newStatus }
            : campaign
        )
      );
    }
  };

  // Get campaign counts by status
  const getStatusCounts = () => {
    const counts = {
      all: campaigns.length,
      need_approve: campaigns.filter((c) => c.status === "need_approve").length,
      active: campaigns.filter((c) => c.status === "active").length,
      suspended: campaigns.filter((c) => c.status === "suspended").length,
      completed: campaigns.filter((c) => c.status === "completed").length,
      cancelled: campaigns.filter((c) => c.status === "cancelled").length,
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  // Pagination logic
  const dataToShow = filteredData.length > 0 ? filteredData : campaigns;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dataToShow.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(dataToShow.length / itemsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const setPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbers = 10;
    const maxVisiblePages = Math.min(totalPages, maxPageNumbers);

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Tafaseel Admin" breadcrumbItem="Campaigns" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id="campaignList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div className="d-flex gap-1">
                          <Button
                            color="primary"
                            className="refresh-btn"
                            onClick={fetchCampaigns}
                            disabled={loading}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            {loading ? "Loading..." : "Refresh"}
                          </Button>
                          <Button
                            color="warning"
                            className="approval-btn"
                            onClick={fetchCampaignsNeedingApproval}
                            disabled={loading}
                          >
                            <i className="ri-time-line align-bottom me-1"></i>
                            Need Approval ({statusCounts.need_approve})
                          </Button>
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end gap-2">
                          <div className="status-filter">
                            <select
                              className="form-select"
                              value={statusFilter}
                              onChange={(e) =>
                                handleStatusFilter(e.target.value)
                              }
                            >
                              <option value="">All Status</option>
                              <option value="need_approve">
                                Need Approval ({statusCounts.need_approve})
                              </option>
                              <option value="active">
                                Active ({statusCounts.active})
                              </option>
                              <option value="suspended">
                                Suspended ({statusCounts.suspended})
                              </option>
                              <option value="completed">
                                Completed ({statusCounts.completed})
                              </option>
                              <option value="cancelled">
                                Cancelled ({statusCounts.cancelled})
                              </option>
                            </select>
                          </div>
                          <div className="search-box">
                            <input
                              type="text"
                              className="form-control search"
                              placeholder="Search campaigns..."
                              value={searchQuery}
                              onChange={handleSearch}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Status Summary Cards */}
                    <Row className="mb-3">
                      <Col>
                        <div className="d-flex gap-3 flex-wrap">
                          <div className="status-card p-2 border rounded">
                            <small className="text-muted">Total</small>
                            <div className="fw-bold">{statusCounts.all}</div>
                          </div>
                          <div className="status-card p-2 border rounded border-warning">
                            <small className="text-warning">
                              Need Approval
                            </small>
                            <div className="fw-bold text-warning">
                              {statusCounts.need_approve}
                            </div>
                          </div>
                          <div className="status-card p-2 border rounded border-success">
                            <small className="text-success">Active</small>
                            <div className="fw-bold text-success">
                              {statusCounts.active}
                            </div>
                          </div>
                          <div className="status-card p-2 border rounded border-info">
                            <small className="text-info">Completed</small>
                            <div className="fw-bold text-info">
                              {statusCounts.completed}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      {loading ? (
                        <div className="text-center p-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                          <p className="mt-2">Loading campaigns...</p>
                        </div>
                      ) : (
                        <table
                          className="table align-middle table-nowrap"
                          id="campaignTable"
                        >
                          <thead className="table-light">
                            <tr>
                              <th className="sort" data-sort="campaign_id">
                                ID
                              </th>
                              <th className="sort" data-sort="title">
                                Campaign
                              </th>
                              <th className="sort" data-sort="business">
                                Business
                              </th>
                              <th className="sort" data-sort="category">
                                Category
                              </th>
                              <th className="sort" data-sort="price">
                                Budget
                              </th>
                              <th className="sort" data-sort="campaign_date">
                                Campaign Date
                              </th>
                              <th className="sort" data-sort="location">
                                Location
                              </th>
                              <th className="sort" data-sort="applications">
                                Applications
                              </th>
                              <th className="sort" data-sort="status">
                                Status
                              </th>
                              <th className="sort" data-sort="views">
                                Views
                              </th>
                              <th className="sort" data-sort="created_at">
                                Created
                              </th>
                              <th className="sort" data-sort="action">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="list form-check-all">
                            {currentItems.length > 0 ? (
                              currentItems.map((campaign) => (
                                <TableItem
                                  key={campaign.id}
                                  campaign={campaign}
                                  handleShow={handleShow}
                                  handleDelete={handleDelete}
                                />
                              ))
                            ) : (
                              <tr>
                                <td colSpan="12" className="text-center p-4">
                                  <div className="text-center">
                                    <lord-icon
                                      src="https://cdn.lordicon.com/msoeawqm.json"
                                      trigger="loop"
                                      colors="primary:#121331,secondary:#08a88a"
                                      style={{ width: "75px", height: "75px" }}
                                    ></lord-icon>
                                    <h5 className="mt-2">No Campaigns Found</h5>
                                    <p className="text-muted mb-0">
                                      {searchQuery || statusFilter
                                        ? `No campaigns found matching your criteria`
                                        : "No campaigns available at the moment."}
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}

                      {!loading &&
                        dataToShow.length === 0 &&
                        !searchQuery &&
                        !statusFilter && (
                          <div className="noresult text-center p-4">
                            <lord-icon
                              src="https://cdn.lordicon.com/msoeawqm.json"
                              trigger="loop"
                              colors="primary:#121331,secondary:#08a88a"
                              style={{ width: "75px", height: "75px" }}
                            ></lord-icon>
                            <h5 className="mt-2">No Campaigns Available</h5>
                            <p className="text-muted mb-0">
                              There are no campaigns in the system yet.
                            </p>
                          </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-center">
                        <div className="pagination-wrap hstack gap-2">
                          <div className="d-flex justify-content-end">
                            <div
                              className="btn-group me-2"
                              role="group"
                              aria-label="Pagination"
                            >
                              <button
                                className="page-item pagination-prev btn btn-primary"
                                onClick={prevPage}
                                type="button"
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>

                              {getPageNumbers().map((pageNumber) => (
                                <button
                                  type="button"
                                  className={`btn ${
                                    pageNumber === currentPage
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  key={pageNumber}
                                  onClick={() => setPage(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              ))}

                              <button
                                disabled={currentPage === totalPages}
                                className="page-item pagination-next btn btn-primary"
                                type="button"
                                onClick={nextPage}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show results info */}
                    {dataToShow.length > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted">
                          Showing {indexOfFirstItem + 1} to{" "}
                          {Math.min(indexOfLastItem, dataToShow.length)} of{" "}
                          {dataToShow.length} campaigns
                        </div>
                        <div className="text-muted">
                          Page {currentPage} of {totalPages}
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Campaign Details Modal */}
          {modal_center && (
            <CampaignDetailsModal
              modal_center={modal_center}
              setmodal_center={setmodal_center}
              tog_center={tog_center}
              campaign={selectedCampaign}
              onStatusUpdate={handleStatusUpdate}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Campaigns;
